import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const CUSTOM_DROPDOWN_CLOSE_DELAY = 320;
const CUSTOM_DROPDOWN_CLOSE_TRANSITION_FALLBACK = 320;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {Map<string, HTMLElement>}
   */
  #customDropdownByTrigger = new Map();

  /**
   * @type {HTMLElement | null}
   */
  #activeCustomDropdown = null;

  /**
   * @type {number | null}
   */
  #customDropdownCloseTimeout = null;

  /**
   * @type {Map<HTMLElement, () => void>}
   */
  #customDropdownCloseCleanups = new Map();

  /**
   * @type {{ x: number; y: number } | null}
   */
  #lastPointerPosition = null;

  /**
   * @type {{ x: number; y: number } | null}
   */
  #previousPointerPosition = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', this.#handleOverflowMenuPointerLeave);
    window.addEventListener('pointermove', this.#handleWindowPointerMove, { passive: true });
    this.#setupCustomDropdowns();
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    window.removeEventListener('pointermove', this.#handleWindowPointerMove);
    this.overflowMenu?.removeEventListener('pointerleave', this.#handleOverflowMenuPointerLeave);
    this.#teardownCustomDropdowns();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#resetMenuState({ resetPointer: true });
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @param {PointerEvent} event
   */
  #handleWindowPointerMove = (event) => {
    this.#previousPointerPosition = this.#lastPointerPosition;
    this.#lastPointerPosition = {
      x: event.clientX,
      y: event.clientY,
    };

    if (!this.#activeCustomDropdown || !this.#state.activeItem) return;

    if (
      this.#isCustomDropdownRegionActive(this.#state.activeItem) ||
      this.#isPointerInCustomDropdownAimRegion(this.#state.activeItem)
    ) {
      this.#cancelCustomDropdownDeactivate();
      return;
    }

    this.#deactivate(this.#state.activeItem);
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#cancelCustomDropdownDeactivate();

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (event instanceof PointerEvent) {
      this.#previousPointerPosition = this.#lastPointerPosition;
      this.#lastPointerPosition = {
        x: event.clientX,
        y: event.clientY,
      };
    }

    if (
      event.type === 'pointerenter' &&
      this.#activeCustomDropdown &&
      this.#state.activeItem &&
      item &&
      item !== this.#state.activeItem &&
      !(
        event.relatedTarget instanceof Node &&
        this.#activeCustomDropdown.contains(event.relatedTarget)
      ) &&
      this.#isPointerMovingTowardDropdown(event) &&
      this.#isPointerInCustomDropdownAimRegion(this.#state.activeItem)
    )
      return;

    if (!item) return;

    if (item == this.#state.activeItem) {
      const customDropdown = this.#findCustomDropdownForItem(item);

      if (
        customDropdown &&
        (customDropdown.hidden || this.dataset.customDropdownActive !== 'true')
      ) {
        this.#showCustomDropdown(customDropdown);
        this.headerComponent?.style.setProperty(
          '--submenu-height',
          `${customDropdown.offsetHeight}px`
        );
        this.style.setProperty('--submenu-opacity', '1');
      }

      return;
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const customDropdown = this.#findCustomDropdownForItem(item);
    let submenu = customDropdown ?? findSubmenu(item);

    if (customDropdown) {
      this.#showCustomDropdown(customDropdown);
    } else {
      this.#hideCustomDropdown();
    }

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty(
                '--submenu-height',
                `${submenu.offsetHeight}px`
              );
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const customDropdown = this.#findCustomDropdownForItem(this.#state.activeItem);
    const menu = customDropdown ?? findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu =
      event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(event.relatedTarget);
    const isMovingToDropdown =
      event.relatedTarget instanceof Node &&
      event.type === 'pointerleave' &&
      menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isMovingToDropdown)
      return;

    if (event.type === 'pointerleave' && customDropdown) {
      this.#scheduleCustomDropdownDeactivate(this.#state.activeItem);
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item || item != this.#state.activeItem) return;
    if (!options.force && this.overflowHovered) return;
    if (!options.force && this.#isCustomDropdownRegionActive(item)) return;

    this.#cancelCustomDropdownDeactivate();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    this.#hideCustomDropdown();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"], [data-desktop-menu-dropdown]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Normalize all menu state after resize, reconnect, or forced teardown so the
   * native overflow background cannot remain hidden without an active dropdown.
   * @param {{ resetPointer?: boolean }} [options]
   */
  #resetMenuState(options = {}) {
    this.#cancelCustomDropdownDeactivate();
    this.#cleanupMutationObserver();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.dataset.customDropdownActive = 'false';
    this.ariaExpanded = 'false';

    this.querySelectorAll('[ref="menuitem"][aria-expanded]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      item.ariaExpanded = 'false';
    });

    this.querySelectorAll('[ref="submenu[]"][data-active]').forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;

      delete submenu.dataset.active;
    });

    this.querySelectorAll('[data-desktop-menu-dropdown]').forEach((dropdown) => {
      if (!(dropdown instanceof HTMLElement)) return;

      this.#cancelCustomDropdownCloseTransition(dropdown);
      delete dropdown.dataset.active;
      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.hidden = true;
    });

    this.#activeCustomDropdown = null;
    this.#state.activeItem = null;

    if (options.resetPointer) {
      this.#lastPointerPosition = null;
      this.#previousPointerPosition = null;
    }
  }

  #setupCustomDropdowns() {
    this.#resetMenuState();
    this.#customDropdownByTrigger.clear();

    const dropdowns = this.querySelectorAll('[data-desktop-menu-dropdown]');
    dropdowns.forEach((dropdown) => {
      if (!(dropdown instanceof HTMLElement)) return;

      this.#cancelCustomDropdownCloseTransition(dropdown);
      dropdown.hidden = true;
      delete dropdown.dataset.active;
      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.addEventListener('pointerenter', this.#handleCustomDropdownPointerEnter);
      dropdown.addEventListener('pointerleave', this.#handleCustomDropdownPointerLeave);
      dropdown.addEventListener('focusin', this.#handleCustomDropdownFocusIn);
      dropdown.addEventListener('focusout', this.#handleCustomDropdownFocusOut);
      dropdown.addEventListener('keydown', this.#handleCustomDropdownKeyDown);

      const triggers = dropdown.dataset.menuDropdownTriggers?.split(',') ?? [];
      triggers
        .map((trigger) => normalizeMenuHandle(trigger))
        .filter(Boolean)
        .forEach((trigger) => this.#customDropdownByTrigger.set(trigger, dropdown));
    });

    this.querySelectorAll('[ref="menuitem"]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const dropdown = this.#findCustomDropdownForItem(item);
      if (!dropdown) return;

      item.dataset.desktopMenuCustomTrigger = 'true';
      item.setAttribute('aria-haspopup', 'true');
      item.setAttribute('aria-controls', dropdown.id);
      item.setAttribute('aria-expanded', 'false');
      item.addEventListener('pointerenter', this.#cancelCustomDropdownDeactivate);
      item.addEventListener('focusin', this.#cancelCustomDropdownDeactivate);
      item.addEventListener('keydown', this.#handleCustomTriggerKeyDown);
    });
  }

  #teardownCustomDropdowns() {
    this.#resetMenuState({ resetPointer: true });

    this.querySelectorAll('[data-desktop-menu-dropdown]').forEach((dropdown) => {
      dropdown.removeEventListener('pointerenter', this.#handleCustomDropdownPointerEnter);
      dropdown.removeEventListener('pointerleave', this.#handleCustomDropdownPointerLeave);
      dropdown.removeEventListener('focusin', this.#handleCustomDropdownFocusIn);
      dropdown.removeEventListener('focusout', this.#handleCustomDropdownFocusOut);
      dropdown.removeEventListener('keydown', this.#handleCustomDropdownKeyDown);
    });

    this.querySelectorAll('[data-desktop-menu-custom-trigger]').forEach((item) => {
      item.removeEventListener('pointerenter', this.#cancelCustomDropdownDeactivate);
      item.removeEventListener('focusin', this.#cancelCustomDropdownDeactivate);
      item.removeEventListener('keydown', this.#handleCustomTriggerKeyDown);
    });

    this.#customDropdownByTrigger.clear();
    this.#activeCustomDropdown = null;
  }

  /**
   * @param {HTMLElement | null | undefined} item
   * @returns {HTMLElement | null}
   */
  #findCustomDropdownForItem(item) {
    if (!(item instanceof HTMLElement)) return null;

    const trigger = normalizeMenuHandle(item.dataset.menuLinkHandle || item.textContent || '');
    return this.#customDropdownByTrigger.get(trigger) ?? null;
  }

  /**
   * @param {HTMLElement} dropdown
   */
  #showCustomDropdown(dropdown) {
    this.#cancelCustomDropdownDeactivate();
    this.#cancelCustomDropdownCloseTransition(dropdown);
    delete dropdown.dataset.switching;

    const isSwitchingDropdown = Boolean(
      this.#activeCustomDropdown && this.#activeCustomDropdown !== dropdown
    );

    if (isSwitchingDropdown) {
      this.#hideCustomDropdown({ immediate: true });
    }

    this.#positionCustomDropdown(dropdown);
    delete dropdown.dataset.active;
    delete dropdown.dataset.closing;

    if (isSwitchingDropdown) {
      dropdown.dataset.switching = '';
    }

    dropdown.hidden = false;

    if (!isSwitchingDropdown && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      void dropdown.offsetHeight;
    }

    dropdown.dataset.active = '';
    this.#activeCustomDropdown = dropdown;
    this.dataset.customDropdownActive = 'true';
  }

  /**
   * Anchor the dropdown to the bottom of the visible header row, not the
   * header group. In this theme the fixed dropdown is positioned in the header
   * containing block, so viewport-level header group variables create a gap.
   * @param {HTMLElement} dropdown
   */
  #positionCustomDropdown(dropdown) {
    const headerRect = this.headerComponent?.getBoundingClientRect();
    const anchorRect = (this.closest('.header__row') ?? this).getBoundingClientRect();

    if (!headerRect) return;

    dropdown.style.setProperty(
      '--desktop-menu-dropdown-top',
      `${Math.max(0, anchorRect.bottom - headerRect.top)}px`
    );
  }

  /**
   * @param {{ immediate?: boolean }} [options]
   */
  #hideCustomDropdown(options = {}) {
    this.#cancelCustomDropdownDeactivate();

    if (!this.#activeCustomDropdown) {
      this.dataset.customDropdownActive = 'false';
      return;
    }

    const dropdown = this.#activeCustomDropdown;
    this.#cancelCustomDropdownCloseTransition(dropdown);
    delete dropdown.dataset.switching;

    delete dropdown.dataset.active;
    this.#activeCustomDropdown = null;
    this.dataset.customDropdownActive = 'false';

    if (options.immediate) {
      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.hidden = true;
      return;
    }

    dropdown.dataset.closing = '';
    dropdown.hidden = false;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.hidden = true;
      return;
    }

    const finishClose = () => {
      this.#customDropdownCloseCleanups.get(dropdown)?.();
      if (!dropdown.hasAttribute('data-closing')) return;

      delete dropdown.dataset.closing;
      delete dropdown.dataset.switching;
      dropdown.hidden = true;
    };

    const handleTransitionEnd = (event) => {
      if (event.target !== dropdown) return;
      if (event.propertyName !== 'opacity' && event.propertyName !== 'transform') return;

      finishClose();
    };

    const closeTimeout = window.setTimeout(finishClose, CUSTOM_DROPDOWN_CLOSE_TRANSITION_FALLBACK);

    const cleanup = () => {
      dropdown.removeEventListener('transitionend', handleTransitionEnd);
      window.clearTimeout(closeTimeout);
      this.#customDropdownCloseCleanups.delete(dropdown);
    };

    dropdown.addEventListener('transitionend', handleTransitionEnd);
    this.#customDropdownCloseCleanups.set(dropdown, cleanup);
  }

  /**
   * @param {HTMLElement} dropdown
   */
  #cancelCustomDropdownCloseTransition(dropdown) {
    this.#customDropdownCloseCleanups.get(dropdown)?.();

    delete dropdown.dataset.closing;
  }

  #handleOverflowMenuPointerLeave = () => {
    if (this.#activeCustomDropdown) {
      this.#scheduleCustomDropdownDeactivate(this.#state.activeItem);
      return;
    }

    this.#deactivate();
  };

  /**
   * Move keyboard focus from a custom top-level trigger into its dropdown.
   * @param {KeyboardEvent} event
   */
  #handleCustomTriggerKeyDown = (event) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const item = event.currentTarget;

    if (event.key === 'Escape') {
      this.#deactivate(item, { force: true });
      return;
    }

    if (event.key !== 'Tab' || event.shiftKey) return;

    const dropdown = this.#findCustomDropdownForItem(item);
    if (!dropdown || dropdown.hidden) return;

    const firstFocusable = getFocusableElements(dropdown)[0];
    if (!firstFocusable) return;

    event.preventDefault();
    firstFocusable.focus();
  };

  #handleCustomDropdownPointerEnter = () => {
    this.#cancelCustomDropdownDeactivate();
  };

  #handleCustomDropdownFocusIn = () => {
    this.#cancelCustomDropdownDeactivate();
  };

  /**
   * @param {PointerEvent} event
   */
  #handleCustomDropdownPointerLeave = (event) => {
    if (
      event.relatedTarget instanceof Node &&
      this.#state.activeItem?.contains(event.relatedTarget)
    )
      return;

    if (this.#activeCustomDropdown?.contains(document.activeElement)) return;

    this.#scheduleCustomDropdownDeactivate(this.#state.activeItem);
  };

  /**
   * @param {FocusEvent} event
   */
  #handleCustomDropdownFocusOut = (event) => {
    if (
      event.relatedTarget instanceof Node &&
      (this.#activeCustomDropdown?.contains(event.relatedTarget) ||
        this.#state.activeItem?.contains(event.relatedTarget))
    )
      return;

    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * Keep reverse tab and Escape behavior coherent once focus is inside a custom dropdown.
   * @param {KeyboardEvent} event
   */
  #handleCustomDropdownKeyDown = (event) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const dropdown = event.currentTarget;
    const activeItem = this.#state.activeItem;

    if (event.key === 'Escape') {
      event.preventDefault();
      activeItem?.focus();
      this.#deactivate(activeItem, { force: true });
      return;
    }

    if (event.key !== 'Tab' || !event.shiftKey || !activeItem) return;

    const firstFocusable = getFocusableElements(dropdown)[0];
    if (firstFocusable && document.activeElement !== firstFocusable) return;

    event.preventDefault();
    activeItem.focus();
  };

  /**
   * Detached custom dropdowns need a small pointer bridge between the trigger
   * and the fixed panel because they do not share one hoverable parent.
   * @param {HTMLElement | null} item
   */
  #scheduleCustomDropdownDeactivate(item, allowBridge = true) {
    if (!item) return;

    this.#cancelCustomDropdownDeactivate();
    this.#customDropdownCloseTimeout = window.setTimeout(() => {
      this.#customDropdownCloseTimeout = null;

      if (this.#isCustomDropdownRegionActive(item)) return;

      if (allowBridge && this.#isPointerInCustomDropdownAimRegion(item)) {
        this.#scheduleCustomDropdownDeactivate(item, false);
        return;
      }

      this.#deactivate(item);
    }, CUSTOM_DROPDOWN_CLOSE_DELAY);
  }

  #cancelCustomDropdownDeactivate = () => {
    if (!this.#customDropdownCloseTimeout) return;

    window.clearTimeout(this.#customDropdownCloseTimeout);
    this.#customDropdownCloseTimeout = null;
  };

  /**
   * @param {HTMLElement | null} item
   * @returns {boolean}
   */
  #isCustomDropdownRegionActive(item) {
    const dropdown = this.#findCustomDropdownForItem(item);

    return Boolean(
      item?.matches(':hover, :focus-within') ||
      dropdown?.matches(':hover, :focus-within') ||
      this.#isPointerInCustomDropdownRegion(item) ||
      dropdown?.contains(document.activeElement)
    );
  }

  /**
   * The fixed dropdown is detached from the top-level menu item, so CSS hover on
   * the menu item is not enough. Treat the trigger and dropdown rectangles as
   * the active pointer region.
   *
   * @param {HTMLElement | null} item
   * @returns {boolean}
   */
  #isPointerInCustomDropdownRegion(item) {
    const pointer = this.#lastPointerPosition;
    const dropdown = this.#findCustomDropdownForItem(item);

    if (!pointer || !item || !dropdown || dropdown.hidden) return false;

    const itemRect = item.getBoundingClientRect();
    const dropdownRect = dropdown.getBoundingClientRect();

    if (dropdownRect.width <= 0 || dropdownRect.height <= 0) return false;

    const isInsideTrigger =
      pointer.x >= itemRect.left &&
      pointer.x <= itemRect.right &&
      pointer.y >= itemRect.top &&
      pointer.y <= itemRect.bottom;
    const isInsideDropdown =
      pointer.x >= dropdownRect.left &&
      pointer.x <= dropdownRect.right &&
      pointer.y >= dropdownRect.top &&
      pointer.y <= dropdownRect.bottom;

    return isInsideTrigger || isInsideDropdown;
  }

  /**
   * A narrow "menu aim" region prevents diagonal movement into a fixed dropdown
   * from accidentally activating a neighboring top-level trigger. This is not a
   * persistent hover state; pointer movement outside this path closes the menu.
   *
   * @param {HTMLElement | null} item
   * @returns {boolean}
   */
  #isPointerInCustomDropdownAimRegion(item) {
    const pointer = this.#lastPointerPosition;
    const dropdown = this.#findCustomDropdownForItem(item);

    if (!pointer || !item || !dropdown || dropdown.hidden) return false;

    const itemRect = item.getBoundingClientRect();
    const dropdownRect = dropdown.getBoundingClientRect();

    if (dropdownRect.width <= 0 || dropdownRect.height <= 0) return false;

    const aimTop = itemRect.top + itemRect.height * 0.72;
    const aimBottom = dropdownRect.top + 10;

    return (
      pointer.x >= dropdownRect.left &&
      pointer.x <= dropdownRect.right &&
      pointer.y >= aimTop &&
      pointer.y <= aimBottom
    );
  }

  /**
   * @param {PointerEvent | null} [event]
   * @returns {boolean}
   */
  #isPointerMovingTowardDropdown(event = null) {
    if (
      event instanceof PointerEvent &&
      Number.isFinite(event.movementY) &&
      event.movementY !== 0
    ) {
      return event.movementY > 0;
    }

    if (!this.#previousPointerPosition || !this.#lastPointerPosition) return false;

    return this.#lastPointerPosition.y > this.#previousPointerPosition.y;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.matches('[slot="more"]') ? element : element.closest('[slot="more"]');

  if (moreItem) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(moreItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  const menuItem = element.closest('.menu-list__list-item') ?? element;
  const item = element.matches('[ref="menuitem"]')
    ? element
    : menuItem.querySelector('[ref="menuitem"]');

  return item instanceof HTMLElement ? item : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Normalizes a menu label to the same shape as Liquid's `handleize` filter.
 * @param {string} value
 * @returns {string}
 */
function normalizeMenuHandle(value) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * @param {HTMLElement} element
 * @returns {HTMLElement[]}
 */
function getFocusableElements(element) {
  return Array.from(element.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((focusable) => focusable instanceof HTMLElement)
    .filter((focusable) => focusable.getClientRects().length > 0);
}
